import { Inject, Injectable, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { fetch } from '@ngrx/router-store/data-persistence';
import { Store } from '@ngrx/store';
import { catchError, exhaustMap, map, of, switchMap } from 'rxjs';
import { delay, filter, mergeMap, tap, withLatestFrom } from 'rxjs/operators';

import { CoreGoogleAnalyticActions } from '@fantaz/core/analytic';
import { RouterActions, RouterSelectors } from '@fantaz/core/router';
import { CoreDialogMessageActions } from '@fantaz/core/state';
import {
  AuthActions,
  AuthSelectors,
  getAuthUser,
  loadUser,
} from '@fantaz/shared/auth';
// local dependencies
import { VERIFICATION_TYPES } from '@fantaz/shared/config';
import {
  AccountService,
  DataAccessAccountService,
  DataAccessUploadService,
  IUploadService,
  TournamentApiService,
  VerificationDuplicateException,
  VerificationEscalateException,
  VerificationIncompleteException,
  VerificationNoneException,
  VerificationRejectedException,
  VerifyUserDto,
} from '@fantaz/shared/data-access/api';
import { GAEventKeys, User } from '@fantaz/shared/data-access/models';
import {
  ColorScheme,
  ColorSchemeService,
} from '@fantaz/shared/services/color-scheme';
import { SnackbarService } from '@fantaz/shared/snackbar';
import { ModalAlertComponent } from '@fantaz/shared/ui/modal-alert';
import { canParseUrl } from '@fantaz/shared/utils';
import {
  ForceKycDialog,
  VerificationDialogComponent,
} from '@fantaz/user-verification/ui/verification-dialog';
import { AttemptsExceededComponent } from '@fantaz/user-verification/ui/verification-failures';
import { VerificationSuccessDialogComponent } from '@fantaz/user-verification/ui/verification-success-dialog';

import * as EditInfoActions from '../edit-info/edit-info.actions';
import { RegisterCreditCardActions } from '../register-credit-card';
import * as UploadPictureActions from '../upload-picture/upload-picture.actions';
import * as VerifyUserActions from './verify-user.actions';
import { VerifyUserInBackground } from './verify-user.actions';
import { RequireVerificationAction } from './verify-user.actions';

@Injectable()
export class VerifyUserEffects {
  #snackbarService = inject(SnackbarService);

  private readonly accountService: AccountService = inject(
    DataAccessAccountService,
  );

  initEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RouterActions.ROUTER_NAVIGATED),
      delay(1000),
      withLatestFrom(this.store.select(getAuthUser)),
      map(([_, user]) => {
        if (user?.verificationStatus?.type === VERIFICATION_TYPES.DUPLICATE) {
          return {
            error: new VerificationDuplicateException([]),
            user,
          };
        }
        if (user?.verificationStatus?.type === VERIFICATION_TYPES.REJECTED) {
          return {
            error: new VerificationRejectedException(),
            user,
          };
        }

        if (user?.verificationStatus?.type === VERIFICATION_TYPES.ESCALATE) {
          return {
            error: new VerificationEscalateException(),
            user,
          };
        }

        if (user?.verificationStatus?.type === VERIFICATION_TYPES.FORCE_KYC) {
          return {
            error: new Error('FORCE_KYC'),
            user,
          };
        }

        if (
          user?.verificationStatus?.type ===
          VERIFICATION_TYPES.FORCE_KYC_DOCUMENT
        ) {
          return {
            error: new Error('FORCE_KYC_DOCUMENT'),
            user,
          };
        }

        return null;
      }),
      filter((err) => {
        if (err) {
          return !window.location.href.includes('user-game-profile');
        }

        return false;
      }),
      map((payload) =>
        VerifyUserActions.verifyUserFailure({
          ...(payload as any),
        }),
      ),
    ),
  );

  /**
   * Effect to handle verification suggestion
   */
  handleVerifySuggestionsEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VerifyUserActions.verifyUserSuggestion),
      withLatestFrom(this.store.select(AuthSelectors.getAuthUser)),
      switchMap(([_, user]) => {
        // Validate if user is already completed the game profile
        const haveGameProfile =
          user?.firstName &&
          user?.lastName &&
          user?.birthday &&
          user?.legalAddress;

        if (user && haveGameProfile) {
          return [VerifyUserInBackground.verifyUser()];
        }
        window.location.href = '/user-game-profile';
        return [
          // TODO: Navigate to user game profile page using core router action
        ];
      }),
    ),
  );

  /**
   * Effect to handle user verification in background
   */
  verifyUserInBackgroundEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VerifyUserInBackground.verifyUser),
      withLatestFrom(this.store.select(AuthSelectors.getAuthUser)),
      map(([_, user]) => {
        return {
          user: new VerifyUserDto({
            firstName: user?.firstName as string,
            lastName: user?.lastName as string,
            email: user?.email as string,
            address: user?.legalAddress as any,
            dateOfBirth: user?.birthday as string,
          }),
        };
      }),
      switchMap(({ user }) => {
        return this.accountService.verifyAccount(user).pipe(
          map((response) => {
            return VerifyUserActions.verifyUserSuccess({ source: 'kyc' });
          }),
          catchError((error) => {
            return of(VerifyUserActions.verifyUserFailure({ error, user }));
          }),
        );
      }),
    ),
  );

  /**
   * Effect to open the verification dialog
   */
  openVerifyDialogEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VerifyUserActions.openVerificationDialog),
      withLatestFrom(
        this.store.select(AuthSelectors.getAuthUser),
        (_, user) => ({
          user: {
            ...user,
          },
        }),
      ),
      exhaustMap(({ user }) => {
        const dialogRef = this.dialog.open(VerificationDialogComponent, {
          disableClose: true,
          data: {
            user,
            attempts: {
              ...user?.verificationStatus?.attempts,
            },
          },
        });
        return dialogRef.afterClosed();
      }),
      mergeMap((result: { option?: string; source?: string; user: User }) => {
        if (result && result.option === 'register-credit-card') {
          return [
            RegisterCreditCardActions.openRegisterCreditCardDialog({}),
            CoreGoogleAnalyticActions.trackEvent({
              event: {
                event: GAEventKeys.USER_VERIFICATION_STATUS,
                verification_action: 'register-credit-card',
                verification_status: 'register-credit-card-selected',
                verification_attempts_failed:
                  result.user?.verificationStatus?.attempts?.current.CC || 0,
              },
            }),
          ];
        }
        if (result && result.option === 'upload-id-picture') {
          return [
            UploadPictureActions.showUploadPictureDialog(),
            CoreGoogleAnalyticActions.trackEvent({
              event: {
                event: GAEventKeys.USER_VERIFICATION_STATUS,
                verification_action: 'upload-id-picture',
                verification_status: 'upload-id-picture-selected',
                verification_attempts_failed:
                  result.user?.verificationStatus?.attempts?.current.DOCUMENT ||
                  0,
              },
            }),
          ];
        }
        if (result && result.option === 'edit-info') {
          return [
            EditInfoActions.openEditInfoDialog(),
            CoreGoogleAnalyticActions.trackEvent({
              event: {
                event: GAEventKeys.USER_VERIFICATION_STATUS,
                verification_action: 'edit-user-info',
                verification_status: 'edit-user-info-selected',
                verification_attempts_failed:
                  result.user?.verificationStatus?.attempts?.current
                    .CUSTOMER_IDENTITY || 0,
              },
            }),
          ];
        }
        if (result && result.source === 'close-button') {
          return [
            CoreGoogleAnalyticActions.trackEvent({
              event: {
                event: GAEventKeys.USER_VERIFICATION_STATUS,
                verification_action: 'user-abandons-verification',
                verification_status: 'close-user-verification-selected',
                verification_attempts_failed:
                  result.user?.verificationStatus?.attempts?.current
                    .CUSTOMER_IDENTITY || 0,
              },
            }),
          ];
        }

        return [];
      }),
    ),
  );

  /**
   * Effect to handle user verification
   */
  handleVerifyUserEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VerifyUserActions.verifyUser),
      withLatestFrom(this.store.select(AuthSelectors.getAuthUser)),
      fetch({
        run: ({ user }, globalUser) => {
          const requestParams = {
            businessName: user.businessName ?? undefined,
            stateOfFormation: user.stateOfFormation ?? undefined,
            classification: user.classification
              ? {
                  name: user.classification.name ?? undefined,
                  type: user.classification.type ?? undefined,
                  otherType: user.other ?? undefined,
                }
              : undefined,
            firstName: user.firstName,
            lastName: user.lastName,
            email: user?.email as string,
            dateOfBirth: user.dateOfBirth,
            address: user.address,
            entity: user.entity,
            ein: user.ein ?? undefined,
            stateRegionDiff: user.differentStateForTaxPurposes === 'yes',
            stateRegionForTax: user.differentStateForTaxPurposes
              ? user.stateRegionForTax ?? undefined
              : undefined,
            correctInformation: user.correctInformation,
            legalAge: user.legalAge,
            differentStateForTaxPurposes: user.differentStateForTaxPurposes,
            legalRepresentative: user.legalRepresentative ?? undefined,
            userUuid: user.userUuid,
          };
          return this.accountService
            .verifyAccount(new VerifyUserDto(requestParams))
            .pipe(
              switchMap(() => {
                return [
                  VerifyUserActions.verifyUserSuccess({ source: 'kyc' }),
                  // TODO: add correct event for user verification success
                ];
              }),
              catchError((error) => {
                // Add request parameters to the error object
                if (error) {
                  error.requestParams = requestParams;
                }
                return [
                  // HACK: This is a hack to get the error key to report to
                  // google analytics. This is a temporary solution until we
                  // have a better way to handle this or move to utils.
                  CoreGoogleAnalyticActions.trackEvent({
                    event: {
                      event: GAEventKeys.USER_VERIFICATION_STATUS,
                      verification_action: 'user-verification-fail',
                      verification_status: 'kyc-user-verification-fail',
                      verification_attempts_failed:
                        globalUser?.verificationStatus?.attempts?.current
                          .CUSTOMER_IDENTITY || 0,
                    },
                  }),
                  VerifyUserActions.verifyUserFailure({ error, user }),
                ];
              }),
            );
        },
        onError: (action, error) => {
          console.error('Error', error);
        },
      }),
    ),
  );

  /**
   * Effect to handle user verification success
   */
  handleVerifyUserSuccessEffect$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(VerifyUserActions.verifyUserSuccess),

        /**
         * Get the redirect URL from the query params and parse it to get the
         * tournament key and type if it exists in the URL and pass it to action
         * payload to be used in the dialog component to redirect the user to
         * the tournament page
         */
        withLatestFrom(
          this.store.select(RouterSelectors.selectQueryParam('redirectTo')),
        ),
        map(([_, redirectTo]) => {
          // HACK: This is a hack to get the tournament key and type from the
          // redirect URL. This is a temporary solution until we have a better
          // way to handle this or move to utils.
          if (redirectTo && redirectTo != '') {
            redirectTo = (
              redirectTo.length ? redirectTo[0] : redirectTo
            ) as string;
            let path = redirectTo;

            if (canParseUrl(redirectTo)) {
              const url = new URL(redirectTo);
              path = url.pathname;
            }
            // parse the tournaments/{type}/{key} from the URL
            const r1 = /tournaments\/([^/]+)\/([^/]+)/;
            const m1 = path.match(r1);
            if (m1?.length) {
              // parse the tournaments/{type}/{key}/{eventSchedule} from the URL
              const r2 = /tournaments\/([^/]+)\/([^/]+)\/([^/]+)/;
              const m2 = path.match(r2);

              if (m2?.length) {
                return {
                  requireVerification: _.requireVerification,
                  redirect: redirectTo,
                  tournament: {
                    type: m2[1],
                    key: m2[2],
                    eventSchedule: m2[3],
                  },
                };
              }
              return {
                requireVerification: _.requireVerification,
                redirect: redirectTo,
                tournament: {
                  type: m1[1],
                  key: m1[2],
                },
              };
            }
          }

          return {
            ..._,
            redirect: redirectTo,
            tournament: null,
          };
        }),
        switchMap(({ redirect, tournament, requireVerification }) => {
          if (tournament) {
            return this.tournamentService
              .getTournamentDetails(tournament.key)
              .pipe(
                map((details) => {
                  // check if tournaments is a free or paid tournament
                  // HACK: This is a hack to generate the action payload for the
                  // dialog component. This is a temporary solution until we
                  // have a better way to handle this or move to utils.
                  if (details.detailsForEventSchedule?.length) {
                    const eventSchedule = details.detailsForEventSchedule.find(
                      (e: any) => e.uuid === tournament.eventSchedule,
                    );

                    if (eventSchedule && eventSchedule.entryAmount > 0) {
                      return {
                        message: `Your player profile is completed${
                          requireVerification ? ' and verified' : ''
                        }. Please load your wallet to participate in <b>${
                          details.name
                        }</b>. Start having fun!`,
                        primaryActionLabel: 'Load My Wallet',
                        primaryActionRoute: '/wallet',
                        secondaryActionLabel: 'Go To The Tournament',
                        secondaryActionRoute: redirect,
                      };
                    }
                  }

                  return {
                    message: `Your player profile is completed${
                      requireVerification ? ' and verified' : ''
                    }. You're all set to start playing <b>${
                      details.name
                    }</b> for free or load your wallet to  participate in paid tournaments. Start having fun!`,
                    primaryActionLabel: 'Go To The Tournament',
                    primaryActionRoute: redirect,
                    secondaryActionLabel: 'Load My Wallet',
                    secondaryActionRoute: '/wallet',
                  };
                }),
                catchError(() => {
                  return of({
                    redirect,
                  });
                }),
              );
          }

          const option = {
            redirect,
            title: 'Thank you!',
            message: `Your player profile is completed${
              requireVerification ? ' and verified' : ''
            }. You're all set to start playing in our free tournaments or load your wallet to participate in paid ones. Start having fun!`,
            primaryActionLabel: 'Load My Wallet',
            primaryActionRoute: '/wallet',
            secondaryActionLabel: 'Check out our tournaments',
            secondaryActionRoute: '/tournaments',
          };

          if (redirect?.includes('wallet')) {
            option.primaryActionRoute = redirect;
          }

          return of(option);
        }),
        tap<{
          message?: string;
          redirect?: string;
          primaryActionRoute?: string;
          primaryActionLabel?: string;
          secondaryActionRoute?: string;
          secondaryActionLabel?: string;
        }>(
          ({
            message,
            primaryActionRoute,
            primaryActionLabel,
            secondaryActionLabel,
            secondaryActionRoute,
          }) => {
            const dialogRef = this.dialog.open(
              VerificationSuccessDialogComponent,
              {
                disableClose: true,
                data: {
                  ...(message && {
                    message,
                  }),
                  ...(primaryActionRoute && {
                    primaryActionRoute,
                  }),
                  ...(primaryActionLabel && {
                    primaryActionLabel,
                  }),
                  ...(secondaryActionRoute && {
                    secondaryActionRoute,
                  }),
                  ...(secondaryActionLabel && {
                    secondaryActionLabel,
                  }),
                },
              },
            );
            return dialogRef.afterClosed();
          },
        ),
      ),
    {
      dispatch: false,
    },
  );
  /**
   * Effect to handle user verification success
   */
  handleVerifyUserSuccessGAEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VerifyUserActions.verifyUserSuccess),
      withLatestFrom(this.store.select(AuthSelectors.getAuthUser)),
      switchMap(([{ source }, user]) => {
        let currentType: 'DOCUMENT' | 'CUSTOMER_IDENTITY' | 'CC' =
          'CUSTOMER_IDENTITY';
        if (source === 'cc') {
          currentType = 'CC';
        } else if (source === 'id') {
          currentType = 'DOCUMENT';
        }
        return [
          CoreGoogleAnalyticActions.trackEvent({
            event: {
              event: GAEventKeys.USER_VERIFICATION_STATUS,
              verification_action: 'user-verified-success',
              verification_status: `${source}-user-verified-success`,
              verification_attempts_failed:
                user?.verificationStatus?.attempts?.current[currentType] || 0,
            },
          }),
          loadUser(),
        ];
      }),
    ),
  );

  /**
   * Effect to handle user verification incomplete failure
   */
  handleVerifyUserFailureEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VerifyUserActions.verifyUserFailure),
      withLatestFrom(this.store.select(AuthSelectors.getAuthUser)),
      filter(([{ error }]) => error instanceof VerificationIncompleteException),
      mergeMap(([{ error }, user]) => [
        // Update the user verification status in the store
        AuthActions.updateUserLocally({
          user: {
            verificationStatus: {
              attempts: {
                ...(error as VerificationIncompleteException).attempts,
              },
            },
          },
        }),
        VerifyUserActions.openVerificationDialog({
          attempts: {
            ...(error as VerificationIncompleteException).attempts,
          },
        }),
      ]),
    ),
  );

  /**
   * Effect to handle user verification escalation
   */
  handleVerifyUserEscalateFailureEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VerifyUserActions.verifyUserFailure),
      withLatestFrom(this.store.select(AuthSelectors.getAuthUser)),
      filter(([{ error }]) => error instanceof VerificationEscalateException),
      mergeMap(([{ error }, user]) => {
        this.dialog.open(AttemptsExceededComponent, {
          disableClose: false,
        });
        return [
          CoreGoogleAnalyticActions.trackEvent({
            event: {
              event: GAEventKeys.USER_VERIFICATION_STATUS,
              verification_action: 'attempts-exceeded',
              verification_status: 'user-verified-fail',
            },
          }),
        ];
      }),
    ),
  );

  /**
   * Effect to handle user verification none error
   */
  handleVerificationErrorEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VerifyUserActions.verifyUserFailure),
      filter(({ error }) => error instanceof VerificationNoneException),
      mergeMap(() => {
        return [
          CoreDialogMessageActions.showDialogMessage({
            message: {
              title: 'Verification Failed',
              content: 'none',
            },
          }),
        ];
      }),
    ),
  );

  /**
   * Effect to handle user duplication
   */
  handleUserDuplicationEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VerifyUserActions.verifyUserFailure),
      filter(({ error }) => error instanceof VerificationDuplicateException),
      exhaustMap(({ error }) => {
        const dialogRef = this.dialog.open(ModalAlertComponent, {
          disableClose: true,
          data: {
            modalType: 'ACCOUNT_ALREADY_EXISTS',
            description: error.message,
            openZenDeskButtonType: 'flat',
          },
        });
        return dialogRef.afterClosed();
      }),
    ),
  );

  /**
   * Effect to handle user rejected
   */
  handleUserRejectedEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VerifyUserActions.verifyUserFailure),
      filter(({ error }) => error instanceof VerificationRejectedException),
      mergeMap(() => {
        return [
          CoreDialogMessageActions.showDialogMessage({
            image:
              this.colorSchemeService.get() === ColorScheme.Dark
                ? '/shared/svg/log-in-dark.svg'
                : '/shared/svg/log-in.svg',
            disableClose: true,
            message: {
              title: 'Oops, there is a problem!',
              content:
                'We have some issue verifying your identity, your profile is locked. Please contact customer support.',
            },
            actions: {
              primaryAction: {
                label: 'Contact Customer Support',
                handler: () => {
                  alert('Contact Customer Support');
                },
              },
            },
          }),
          CoreGoogleAnalyticActions.trackEvent({
            event: {
              event: GAEventKeys.USER_VERIFICATION_STATUS,
              verification_action: 'rejected',
              verification_status: 'user-verified-fail',
            },
          }),
        ];
      }),
    ),
  );

  /**
   * Effect to show required dialog to continue to kyc verification page
   */
  showRequiredDialogEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(VerifyUserActions.verifyUserFailure),
      filter(({ error }) => {
        return (error as Error).message.includes('FORCE_KYC');
      }),
      exhaustMap(({ error }) => {
        const dialogRef = this.dialog.open(ForceKycDialog, {
          disableClose: true,
          data: {
            url:
              (error as Error).message === 'FORCE_KYC_DOCUMENT'
                ? '/user-game-profile?requireVerification=true&kyc=document'
                : '/user-game-profile?requireVerification=true&kyc=all',
          },
        });
        return dialogRef.afterClosed();
      }),
    );
  });

  constructor(
    private readonly actions$: Actions,
    private readonly dialog: MatDialog,
    private readonly store: Store,
    private colorSchemeService: ColorSchemeService,
    private readonly tournamentService: TournamentApiService,
    @Inject(DataAccessUploadService)
    private readonly uploadService: IUploadService,
  ) {}
}
